import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";

import { OtpCodeInput } from "@/components/otp-code-input";
import { StatusBanner } from "@/components/status-banner";
import { PasswordInput } from "@/components/ui/password-input";
import { MIN_PASSWORD_LENGTH } from "@/features/auth/types";
import { resetPassword } from "@/lib/apiSdk";
import { Button } from "@scribe/ui/button";
import { Card, CardContent } from "@scribe/ui/card";
import { Input } from "@scribe/ui/input";
import { Label } from "@scribe/ui/label";

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  validateSearch: z.object({ email: z.string().optional() }),
  component: ResetPasswordPage,
});

const schema = z
  .object({
    email: z.email("Enter a valid email address"),
    code: z.string().trim().length(6, "Enter the 6-digit code"),
    newPassword: z
      .string()
      .min(MIN_PASSWORD_LENGTH, `At least ${MIN_PASSWORD_LENGTH} characters`),
    confirmPassword: z.string().min(1, "Confirm your password"),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type Values = z.infer<typeof schema>;

function ResetPasswordPage() {
  const { email: emailFromSearch } = Route.useSearch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: emailFromSearch ?? "",
      code: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = form.handleSubmit(async (v) => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      await resetPassword(v.email.trim(), v.code.trim(), v.newPassword);
      // The reset revoked every session and cleared the trusted-device window, so
      // the user signs in fresh — and will be asked for a code again.
      void navigate({
        to: "/signin",
        search: { redirect: undefined },
      });
    } catch (err: unknown) {
      const status =
        typeof err === "object" && err && "status" in err
          ? Number((err as { status?: unknown }).status)
          : undefined;
      if (status === 429) {
        setError("Too many attempts. Please wait a moment and try again.");
      } else if (status === 401 || status === 400) {
        setError(
          "That code isn't right, or it has expired. Request a new one and try again."
        );
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  });

  return (
    <div className="flex h-screen w-full">
      <div className="bg-background lg:bg-muted flex w-full flex-col justify-center p-0 lg:p-8">
        <Card className="bg-background lg:bg-card mx-auto flex min-h-screen w-full max-w-none flex-col justify-center overflow-hidden rounded-none border-none py-0 shadow-none lg:min-h-0 lg:max-w-sm lg:rounded-xl lg:border lg:py-6 lg:shadow-sm">
          <CardContent className="p-8">
            <form className="flex flex-col gap-6" onSubmit={onSubmit}>
              <div className="bg-primary/10 mx-auto flex size-16 items-center justify-center rounded-full">
                <KeyRound className="text-primary size-8" />
              </div>

              <div className="flex flex-col gap-2 text-center">
                <h1 className="text-2xl font-semibold">
                  Choose a new password
                </h1>
                <p className="text-muted-foreground text-sm">
                  Enter the code we emailed you, then pick a new password.
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  className="h-11"
                  autoComplete="email"
                  aria-invalid={!!form.formState.errors.email}
                  {...form.register("email")}
                />
                {form.formState.errors.email && (
                  <p className="text-destructive text-xs">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="code" className="text-sm font-medium">
                  Reset code
                </Label>
                <Controller
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <OtpCodeInput
                      value={field.value}
                      onChange={field.onChange}
                      disabled={loading}
                      isPending={loading}
                    />
                  )}
                />
                {form.formState.errors.code && (
                  <p className="text-destructive text-xs">
                    {form.formState.errors.code.message}
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="new-password" className="text-sm font-medium">
                  New password
                </Label>
                <PasswordInput
                  id="new-password"
                  placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  className="h-11"
                  autoComplete="new-password"
                  aria-invalid={!!form.formState.errors.newPassword}
                  {...form.register("newPassword")}
                />
                {form.formState.errors.newPassword && (
                  <p className="text-destructive text-xs">
                    {form.formState.errors.newPassword.message}
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label
                  htmlFor="confirm-password"
                  className="text-sm font-medium"
                >
                  Confirm password
                </Label>
                <PasswordInput
                  id="confirm-password"
                  placeholder="Re-enter your new password"
                  className="h-11"
                  autoComplete="new-password"
                  aria-invalid={!!form.formState.errors.confirmPassword}
                  {...form.register("confirmPassword")}
                />
                {form.formState.errors.confirmPassword && (
                  <p className="text-destructive text-xs">
                    {form.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <StatusBanner error={error} successMsg={null} />

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 size-4 animate-spin" />}
                {loading ? "Updating..." : "Set new password"}
              </Button>

              <div className="text-center">
                <Link
                  to="/signin"
                  className="text-muted-foreground hover:text-foreground inline-flex items-center text-sm"
                >
                  <ArrowLeft className="mr-1 size-3" />
                  Back to sign in
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
